import { zodResolver } from "@hookform/resolvers/zod";
import { type LoginInput, loginSchema } from "@shelflist/shared";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { PublicLayout } from "@/components/public-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { meQuery } from "@/lib/queries";
import { useAfterSignIn } from "@/lib/session";

const searchSchema = z.object({
  // Only same-app paths are accepted to avoid open redirects.
  redirect: z
    .string()
    .regex(/^\/(?!\/)/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation(["auth", "common"]);
  const { redirect: redirectTo } = Route.useSearch();
  const errorMessage = useErrorMessage();
  const afterSignIn = useAfterSignIn();
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const login = useMutation({
    mutationFn: authApi.signIn,
    meta: { handlesErrors: true },
    onSuccess: () => afterSignIn(redirectTo ?? "/"),
  });
  const { errors } = form.formState;

  return (
    <PublicLayout title={t("login.title")} description={t("login.description")}>
      <form
        noValidate
        className="grid gap-4"
        onSubmit={form.handleSubmit((values) => login.mutate(values))}
      >
        <FormError message={login.error ? errorMessage(login.error) : null} />
        <Field id="email" label={t("common:fields.email")} error={errors.email?.message}>
          <Input
            {...fieldA11y("email", errors.email?.message)}
            type="email"
            autoComplete="username"
            autoFocus
            {...form.register("email")}
          />
        </Field>
        <Field id="password" label={t("common:fields.password")} error={errors.password?.message}>
          <Input
            {...fieldA11y("password", errors.password?.message)}
            type="password"
            autoComplete="current-password"
            {...form.register("password")}
          />
        </Field>
        <Button type="submit" disabled={login.isPending}>
          {t("login.submit")}
        </Button>
        <p className="text-muted-foreground text-sm">{t("login.noAccount")}</p>
      </form>
    </PublicLayout>
  );
}
